<template>
  <el-form
    ref="form"
    size="medium"
    v-loading="loading"
    :model="form"
    :rules="rules"
    :validate-on-rule-change="true"
    label-width="150px"
  >
    <div class="section-title">基础信息</div>
    <el-row>
      <el-col :span="12">
        <el-form-item :label="$t('person.role')" prop="roleType">
          <el-select v-model="form.roleType" class="form-field" :disabled="userInfo.level > UserLevel.REGISTERING.code">
            <el-option
              v-for="(name, key) in roleTypeMap"
              :key="key"
              :label="$t(`person.roleMap[${key}]`)"
              :value="+key"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item :label="$t('person.isSign')" prop="signStatus">
          <el-select v-model="form.signStatus" class="form-field" :disabled="activeUser">
            <el-option v-for="(name, key) in SIGN_STATUS.codeNameMap" :key="key" :label="name" :value="+key"></el-option>
          </el-select>
        </el-form-item>
      </el-col>

      <div class="section-title">联系人信息</div>
      <el-col :span="12">
        <el-form-item label="企业微信名" prop="rtxName" key="rtxName">
          <el-input v-model="form.rtxName" placeholder="输入企业微信名字" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item :label="$t('person.email')" prop="email">
          <el-input
            v-model="form.email"
            :placeholder="$t('person.emailPlaceholder')"
            class="form-field"
            type="email"
            autocomplete
          ></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item :label="$t('person.address')" prop="cpAddress">
          <el-input v-model="form.cpAddress" :placeholder="$t('person.addressPlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="12">
        <el-form-item :label="$t('person.phone')" prop="phoneNum">
          <el-input v-model="form.phoneNum" :placeholder="$t('person.phonePlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>
      <el-col :span="24">
        <el-form-item :label="$t('person.qq')" prop="qqNum">
          <el-input v-model="form.qqNum" :placeholder="$t('person.qqPlaceholder')" class="form-field"></el-input>
        </el-form-item>
      </el-col>

      <!-- 商务角色填写 -->
      <template v-if="activeUser && form.roleType === 2">
        <div class="section-title">其他信息</div>
        <el-col :span="24">
          <el-form-item>
            <template #label>
              关联厂牌
              <el-tooltip
                content="关联后可查看厂牌上传的作品和数据"
                placement="top"
                effect="dark"
              >
                <i class="el-icon-info" />
              </el-tooltip>
            </template>
            <div>
              <i class="el-icon-plus" @click="handleAddRelateCompany" />
            </div>
            <ul>
              <li v-for="(relate, index) in form.relateCompanyList" :key="relate.tempId">
                <el-select
                  v-model="relate.relateCompanyId"
                  placeholder="搜索关联公司"
                  filterable
                  :disabled="!!relate.id"
                  @change="handleRelateChange(relate, $event)"
                >
                  <el-option
                    v-for="item in relateCompanyOtions"
                    :key="item.id"
                    :label="item.relateCompanyName"
                    :value="item.relateCompanyId"
                  ></el-option>
                </el-select>
                <i class="el-icon-delete" @click="handleRemoveRelate(relate, index)" />
              </li>
            </ul>
          </el-form-item>
        </el-col>
      </template>
    </el-row>
  </el-form>
</template>

<script>
import { omit } from 'lodash-es'
import { mapGetters } from 'vuex'
import companyService from '@/api/company'
import { emailValidate } from '@/utils/validate'
import { UserLevel } from '@/utils/constants'
import { ROLE_TYPE_ENUM, SIGN_STATUS, Editor } from '../const'

const emailValidator = (rule, value, cb) => {
  if (emailValidate(value)) {
    cb()
  } else {
    cb(new Error('请输入正确的邮箱'))
  }
}

export default {
  name: 'OtherEditForm',
  props: {
    defaultData: Object,
    loading: Boolean,
  },
  data() {
    return {
      UserLevel,
      SIGN_STATUS,

      form: new Editor(),
      rules: {
        roleType: [
          { required: true, message: '请选择用户类型', trigger: 'change' }
        ],
        rtxName: [
          { required: true, message: '请填写企业微信名', trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请填写邮箱', trigger: 'blur' },
          { validator: emailValidator, trigger: 'blur' }
        ],
        phoneNum: [
          { required: true, message: '请填写联系电话', trigger: 'blur' },
        ],
      },
      submitting: false,
      relateCompanyOtions: []
    }
  },
  computed: {
    ...mapGetters([
      'userInfo',
      'relateCompanyList',
      'editorObj',
    ]),
    /**
     * @returns {Boolean}
     */
    activeUser() {
      return this.userInfo.level === UserLevel.PASS.code
    },
    /**
     * @returns {Object}
     */
    roleTypeMap() {
      return omit(ROLE_TYPE_ENUM.codeNameMap, [ROLE_TYPE_ENUM.COMPANY.code])
    },
    isEdit() {
      return !!this.defaultData
    }
  },
  created() {
    if (this.defaultData) {
      this.form = new Editor(this.defaultData)
    }

    if (this.activeUser && this.userInfo.roleType === ROLE_TYPE_ENUM.BUSINESS.code) {
      this.fetchCompanyList()
    }
  },
  methods: {
    async fetchCompanyList() {
      try {
        const result = await companyService.search()

        this.relateCompanyOtions = result.list.map(item => {
          return {
            relateCompanyName: item.companyName,
            relateCompanyId: item.companyId,
            id: item.id
          }
        })
      } catch (error) {
        this.$message({
          type: 'error',
          message: `请求关联公司列表异常. ${error.message}`
        })
      }
    },
    handleRelateChange(relateObj, val) {
      const selected = this.relateCompanyOtions.find(item => item.relateCompanyId === val)
      relateObj.relateCompanyName = selected.relateCompanyName
    },

    handleAddRelateCompany() {
      this.form.relateCompanyList.push({
        tempId: `${Math.random()}`,
        relateCompanyName: '',
        relateCompanyId: ''
      })
    },
    handleRemoveRelate(relateCP, index) {
      this.$alert(`确认删除关联厂牌（${relateCP.relateCompanyName}）`, '提示', {
        type: 'warning'
      }).then(_ => {
        this.form.relateCompanyList.splice(index, 1)
      }).catch(_ => {})
    },

    async genData() {
      const valid = await this.$refs.form.validate().catch(_ => {
        this.$alert('有字段未填写完整, 请检查', '提示', { type: 'warning' })
      })

      if (!valid) throw new Error('表单校验失败')

      const postData = this.form.getBackendData()

      return postData
    }
  }
}
</script>

<style scoped lang="scss">
.form-field {
  width: 260px;
}
</style>
